// src/pages/StartupPage/utils/analyticsService.js
import React from 'react';
import { 
  FaUsers, 
  FaEye, 
  FaDollarSign, 
  FaCheckCircle, 
  FaArrowUp, 
  FaArrowDown,
  FaUserPlus 
} from 'react-icons/fa';
import { 
  getFirestore,
  setDoc,
  serverTimestamp, 
  doc, 
  getDoc,
  collection,
  getDocs,
  query,
  where,
  getCountFromServer,
  arrayUnion,
arrayRemove} from 'firebase/firestore';
import { db } from '../../../Firebase/firebase';

// src/pages/StartupPage/utils/analyticsService.js
import { 
  // ... existing imports ...
  increment,
  writeBatch,
  Timestamp
} from 'firebase/firestore';

// Initialize analytics for a new startup
export const initializeStartupAnalytics = async (startupId) => {
  const batch = writeBatch(db);
  
  // Main analytics document
  const analyticsRef = doc(db, 'analytics', startupId);
  batch.set(analyticsRef, {
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  // Current stats subdocument
  const currentRef = doc(db, 'analytics', startupId, 'current', 'stats');
  batch.set(currentRef, {
    totalViews: 0,
    uniqueVisitors: 0,
    followers: 0,
    updatedAt: serverTimestamp()
  });

  await batch.commit();
};

export const trackView = async (startupId, userId) => {
  try {
    const batch = writeBatch(db);
    const currentRef = doc(db, 'analytics', startupId, 'current', 'stats');
    
    // First check if the document exists
    const currentSnap = await getDoc(currentRef);
    
    if (!currentSnap.exists()) {
      // Initialize analytics if it doesn't exist
      await initializeStartupAnalytics(startupId);
    }

    // Now proceed with tracking
    batch.update(currentRef, {
      totalViews: increment(1),
      updatedAt: serverTimestamp()
    });

    if (userId) {
      const visitorRef = doc(db, 'analytics', startupId, 'visitors', userId);
      batch.set(visitorRef, {
        lastVisited: serverTimestamp(),
        visitCount: increment(1)
      }, { merge: true });

      const visitorDoc = await getDoc(visitorRef);
      if (!visitorDoc.exists()) {
        batch.update(currentRef, {
          uniqueVisitors: increment(1)
        });
      }
    }

    await batch.commit();
  } catch (error) {
    console.error('Error tracking view:', error);
    // Consider adding error reporting here
  }
};
// Add this to the end of analyticsService.js (before the last closing brace)
export const renderChange = (change, changeType) => {
  if (!change) return null;
  const isPositive = changeType === 'positive';
  return (
    <span className={`ml-2 text-xs font-semibold flex items-center ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
      {isPositive ? <FaArrowUp className="mr-1" /> : <FaArrowDown className="mr-1" />}
      {change}
    </span>
  );
};
// Update the trackFollowerChange function to work with your user collection structure
export const trackFollowerChange = async (startupId, userId, isFollowing) => {
  const batch = writeBatch(db);
  const currentRef = doc(db, 'analytics', startupId, 'current', 'stats');
  const followerRef = doc(db, 'analytics', startupId, 'followers', userId);
  const userRef = doc(db, 'users', userId);

  if (isFollowing) {
    // Add to user's followedStartups
    batch.update(userRef, {
      followedStartups: arrayUnion(startupId)
    });
    
    // Add to analytics followers
    batch.set(followerRef, {
      userId,
      followedAt: serverTimestamp()
    });
    
    // Update follower count
    batch.update(currentRef, {
      followers: increment(1),
      updatedAt: serverTimestamp()
    });
  } else {
    // Remove from user's followedStartups
    batch.update(userRef, {
      followedStartups: arrayRemove(startupId)
    });
    
    // Remove from analytics followers
    batch.delete(followerRef);
    
    // Update follower count
    batch.update(currentRef, {
      followers: increment(-1),
      updatedAt: serverTimestamp()
    });
  }

  await batch.commit();
};

// Update getAnalyticsData to count followers from users collection
export const getAnalyticsData = async (startupId) => {
  try {
    // Get current stats
    const currentSnap = await getDoc(doc(db, 'analytics', startupId, 'current', 'stats'));
    if (!currentSnap.exists()) {
      await initializeStartupAnalytics(startupId);
      return getAnalyticsData(startupId);
    }
    const current = currentSnap.data();

    // Get actual follower count from users collection
    const followersQuery = query(
      collection(db, 'users'),
      where('followedStartups', 'array-contains', startupId)
    );
    const followersSnapshot = await getCountFromServer(followersQuery);
    const actualFollowerCount = followersSnapshot.data().count;

    // Get historical data (last 30 days)
    const thirtyDaysAgo = Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
    const historyQuery = query(
      collection(db, 'analytics', startupId, 'history'),
      where('snapshotDate', '>=', thirtyDaysAgo)
    );
    const historySnap = await getDocs(historyQuery);
    const historyData = historySnap.docs.map(doc => doc.data());

    // Calculate previous period averages
    const previousPeriod = historyData.reduce((acc, snap) => {
      return {
        views: acc.views + (snap.totalViews || 0),
        visitors: acc.visitors + (snap.uniqueVisitors || 0),
        followers: acc.followers + (snap.followers || 0),
        count: acc.count + 1
      };
    }, { views: 0, visitors: 0, followers: 0, count: 0 });

    const calculateChange = (currentVal, previousVal) => {
      if (previousVal === 0) {
        if (currentVal === 0) return '0%';
        return `+${currentVal * 100}%`; // Show absolute increase when starting from zero
      }
      
      const avgPrevious = previousVal / Math.max(1, previousPeriod.count);
      const change = ((currentVal - avgPrevious) / avgPrevious) * 100;
      return `${change >= 0 ? '+' : ''}${Math.round(change)}%`;
    };
    return [
      {
        id: 'visitors',
        title: 'Unique Visitors (Last 30d)',
        value: current.uniqueVisitors.toLocaleString(),
        change: calculateChange(current.uniqueVisitors, previousPeriod.visitors),
        changeType: current.uniqueVisitors >= (previousPeriod.visitors / Math.max(1, previousPeriod.count)) ? 'positive' : 'negative',
        icon: <FaUsers className="text-blue-500" />,
        description: 'Unique users who viewed your startup profile.',
      },
      {
        id: 'views',
        title: 'Total Profile Views',
        value: current.totalViews.toLocaleString(),
        change: calculateChange(current.totalViews, previousPeriod.views),
        changeType: current.totalViews >= (previousPeriod.views / Math.max(1, previousPeriod.count)) ? 'positive' : 'negative',
        icon: <FaEye className="text-green-500" />,
        description: 'Total number of times your profile was viewed.',
      },
      {
        id: 'followers',
        title: 'Total Followers',
        value: actualFollowerCount.toLocaleString(), // Use the actual count from users collection
        change: calculateChange(actualFollowerCount, previousPeriod.followers),
        changeType: actualFollowerCount >= (previousPeriod.followers / Math.max(1, previousPeriod.count)) ? 'positive' : 'negative',
        icon: <FaUserPlus className="text-pink-500" />,
        description: 'Users who follow your startup for updates.',
      }
    ];
  } catch (error) {
    console.error('Error fetching analytics:', error);
    return [];
  }
};